import { Box, HStack, VStack, Text, Button, Badge } from "native-base";
import { formatMoney, formatOrderTime } from "../utils/orderDisplay";

/**
 * Renders one order from either collection. `order` is the display shape from
 * utils/orderDisplay (fromLegacyOrder / fromIntegratedOrder). Callbacks receive
 * `order.raw`, the original Firestore doc, so legacy actions are unchanged.
 * Integrated (POS) orders are read-only: their status is managed by the POS.
 */
const OrderCard = ({
  order,
  userData,
  onSetStatus,
  onCancel,
  onRaiseConcern,
}) => {
  const item = order.raw;
  const user = userData?.find((u) => u.userId === order.customerUserId);

  const hasAlcoholicItems = order.items.some((i) => i.alcoholic);

  const formatVerifiedDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      return new Date(dateStr).toLocaleDateString("en-AU", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "";
    }
  };

  const showActions = !order.readOnly && order.status !== "completed";

  return (
    <Box
      ml={2}
      w="100%"
      shadow={5}
      borderRadius={25}
      padding={5}
      bg="coolGray.200"
    >
      {(order.posName || order.orderNumber) && (
        <HStack space={2} mb={2} alignItems="center">
          {order.posName && (
            <Badge colorScheme="info" variant="solid">
              {`${order.posName} order`}
            </Badge>
          )}
          {order.orderNumber && <Text bold>{`#${order.orderNumber}`}</Text>}
        </HStack>
      )}
      <HStack alignItems="center" justifyContent="space-between">
        <HStack flexWrap="wrap" flex={1} space={3}>
          <VStack flex={1} minW="150px">
            <Text bold color="pink.700">
              Table No
            </Text>
            <Text>{order.table || "—"}</Text>
          </VStack>

          <VStack flex={1} minW="150px">
            <Text bold color="pink.700">
              Dish Name
            </Text>
            {order.items.map((ite, i) => (
              <HStack key={i} alignItems="center" space={1}>
                <Text>{ite.quantity > 1 ? `${ite.quantity} × ${ite.name}` : ite.name}</Text>
                {ite.alcoholic && (
                  <Badge colorScheme="warning" variant="subtle" size="sm">
                    18+
                  </Badge>
                )}
              </HStack>
            ))}
          </VStack>

          <VStack flex={1} minW="150px">
            <Text bold color="pink.700">
              Dish Options
            </Text>
            {order.items.map((ite, i) => (
              <VStack key={i}>
                {ite.options.map((opt, i2) => (
                  <Text key={i2}>{opt}</Text>
                ))}
              </VStack>
            ))}
          </VStack>

          <VStack flex={1} minW="150px">
            <Text bold color="pink.700">
              First Name
            </Text>
            <Text>{order.customerName || user?.userFirstName || "User not found"}</Text>
          </VStack>

          <VStack flex={1} minW="150px">
            <Text bold color="pink.700">
              DineIn / Pickup
            </Text>
            <Text>{order.serviceLabel || "—"}</Text>
          </VStack>

          <VStack flex={1} minW="150px">
            <Text bold color="pink.700">
              Accessibility
            </Text>
            <Text>{order.accessibilityNotes || user?.userDisabledType || ""}</Text>
          </VStack>

          <VStack flex={1} minW="150px">
            <Text bold color="pink.700">
              Car Rego
            </Text>
            <Text>{user?.userCarRego || ""}</Text>
          </VStack>

          <VStack flex={1} minW="150px">
            <Text bold color="pink.700">
              Price
            </Text>
            {order.items.map((ite, i) => (
              <Text key={i}>{formatMoney(ite.price)}</Text>
            ))}
          </VStack>

          <VStack flex={1} minW="150px">
            <Text bold color="pink.700">
              Total
            </Text>
            <Text>{formatMoney(order.total)}</Text>
          </VStack>

          <VStack flex={1} minW="150px">
            <Text bold color="pink.700">
              Status
            </Text>
            <Text>{order.statusLabel}</Text>
          </VStack>

          <VStack flex={1} minW="150px">
            <Text bold color="pink.700">
              Time
            </Text>
            <Text>{formatOrderTime(order.time)}</Text>
          </VStack>
        </HStack>

        {/* Action Buttons (legacy orders only) */}
        {showActions && (
          <VStack space={1} alignItems="center">
            <HStack space={2}>
              <Button
                isDisabled={item.OrderStatus === "preparing"}
                w="120px"
                onPress={() => onSetStatus(item, "preparing")}
                colorScheme="orange"
                size="xs"
              >
                Set to Preparing
              </Button>
              <Button
                w="120px"
                onPress={() => onSetStatus(item, "completed")}
                colorScheme="green"
                size="xs"
              >
                Move to Completed
              </Button>
            </HStack>

            <HStack space={2}>
              <Button
                colorScheme="red"
                size="xs"
                w="120px"
                onPress={() => onRaiseConcern(item)}
              >
                Raise Concern
              </Button>
              <Button
                colorScheme="red"
                size="xs"
                w="120px"
                onPress={() => onCancel(item)}
              >
                Cancel
              </Button>
            </HStack>
          </VStack>
        )}
        {order.readOnly && (
          <VStack maxW="200px" alignItems="center">
            <Text fontSize="sm" color="coolGray.700" textAlign="center">
              {`Status is managed in ${order.posName || "your POS"}.`}
            </Text>
          </VStack>
        )}
      </HStack>

      {/* Age Verification Status — only shown for orders with alcoholic items */}
      {hasAlcoholicItems && (
        <Box mt={3} p={3} bg="amber.50" borderRadius={8}>
          {order.ageVerification?.verified === true ? (
            <Text fontSize="sm" color="green.700">
              Age check: Verified via Piatto (
              {formatVerifiedDate(order.ageVerification.verifiedDate)})
            </Text>
          ) : (
            <Text fontSize="sm" color="orange.700" bold>
              Age check: Not verified — please check customer ID for alcoholic
              items.
            </Text>
          )}
          <Text fontSize="xs" color="gray.600" mt={1}>
            RSA reminder: Venue staff retain responsibility under applicable
            liquor licensing laws.
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default OrderCard;
